import { useState } from "react";
import { useLocation, Link } from "wouter";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Plus,
  Trash2,
} from "lucide-react";

type Protocol = "anthropic" | "openai-chat" | "openai-response";

interface ProviderBasics {
  name: string;
  slug: string;
  base_url: string;
  protocol: Protocol;
  backend: "direct" | "vertex";
  project: string;
  location: string;
}

interface ProviderModelDraft {
  name: string;
  upstream_model_name: string;
  protocol: Protocol;
  context_window: number;
  input_cost_per_ktok: number;
  output_cost_per_ktok: number;
  cache_write_cost_per_ktok: number;
  cache_read_cost_per_ktok: number;
  rpm_limit?: number | null;
  tpm_limit?: number | null;
}

const emptyModel = (parentProtocol: Protocol): ProviderModelDraft => ({
  name: "",
  upstream_model_name: "",
  protocol: parentProtocol,
  context_window: 200000,
  input_cost_per_ktok: 0,
  output_cost_per_ktok: 0,
  cache_write_cost_per_ktok: 0,
  cache_read_cost_per_ktok: 0,
  rpm_limit: null,
  tpm_limit: null,
});

const TOTAL_STEPS = 4;

export default function ProviderWizard() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [basics, setBasics] = useState<ProviderBasics>({
    name: "",
    slug: "",
    base_url: "",
    protocol: "anthropic",
    backend: "direct",
    project: "",
    location: "",
  });
  const [apiKey, setApiKey] = useState("");
  const [googleServiceAccountJson, setGoogleServiceAccountJson] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [models, setModels] = useState<ProviderModelDraft[]>([emptyModel("anthropic")]);

  const updateModel = (idx: number, patch: Partial<ProviderModelDraft>) => {
    setModels((arr) => arr.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  };

  const addModel = () => setModels((arr) => [...arr, emptyModel(basics.protocol)]);
  const removeModel = (idx: number) =>
    setModels((arr) => (arr.length > 1 ? arr.filter((_, i) => i !== idx) : arr));

  const validateStep = (s: number): string | null => {
    if (s === 1) {
      if (!basics.name.trim()) return "Provider name is required";
      if (!basics.slug.trim()) return "Slug is required";
      if (!/^[a-z0-9-]+$/.test(basics.slug)) {
        return "Slug must be lowercase letters, numbers, or hyphens";
      }
      if (basics.backend === "direct") {
        if (!basics.base_url.trim()) return "Base URL is required";
        try {
          new URL(basics.base_url);
        } catch {
          return "Base URL must be a valid URL";
        }
      } else {
        if (!basics.project.trim()) return "GCP Project ID is required";
        if (!basics.location.trim()) return "GCP Location is required";
      }
    }
    if (s === 2) {
      if (basics.backend === "direct") {
        if (!apiKey.trim()) return "API Key is required";
      } else {
        if (!googleServiceAccountJson.trim()) return "Google Service Account JSON is required";
        try {
          JSON.parse(googleServiceAccountJson);
        } catch {
          return "Google Service Account JSON must be valid JSON";
        }
      }
    }
    if (s === 3) {
      if (models.length === 0) return "At least one Provider Model is required";
      for (let i = 0; i < models.length; i++) {
        const m = models[i];
        if (!m.name.trim()) return `Model #${i + 1}: name is required`;
        if (!m.upstream_model_name.trim())
          return `Model #${i + 1}: upstream model name is required`;
        if (m.protocol !== basics.protocol)
          return `Model #${i + 1}: protocol must match the provider protocol (${basics.protocol})`;
        if (!Number.isFinite(m.context_window) || m.context_window <= 0)
          return `Model #${i + 1}: context window must be > 0`;
      }
    }
    if (s === 2) {
      if (!apiKey.trim()) {
        return "API Key is required";
      }
    }
    if (s === 3) {
      if (models.length === 0) return "At least one Provider Model is required";
      for (let i = 0; i < models.length; i++) {
        const m = models[i];
        if (!m.name.trim()) return `Model #${i + 1}: name is required`;
        if (!m.upstream_model_name.trim())
          return `Model #${i + 1}: upstream model name is required`;
        if (m.protocol !== basics.protocol)
          return `Model #${i + 1}: protocol must match the provider protocol (${basics.protocol})`;
        if (!Number.isFinite(m.context_window) || m.context_window <= 0)
          return `Model #${i + 1}: context window must be > 0`;
      }
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) {
      toast.error(err);
      return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    for (let s = 1; s <= 3; s++) {
      const err = validateStep(s);
      if (err) {
        toast.error(err);
        setStep(s);
        return;
      }
    }
    setSubmitting(true);
    try {
      const providerPayload: any = {
        name: basics.name.trim(),
        slug: basics.slug.trim(),
        protocol: basics.protocol,
        backend: basics.backend,
      };
      if (basics.backend === "direct") {
        providerPayload.base_url = basics.base_url.trim();
        providerPayload.api_key = apiKey;
      } else {
        providerPayload.extra_config = {
          project: basics.project.trim(),
          location: basics.location.trim(),
        };
        providerPayload.google_service_account_json = googleServiceAccountJson;
      }
      const provider = await adminApi.createProvider(providerPayload);

      const errors: string[] = [];
      for (const m of models) {
        try {
          await adminApi.createProviderModel(provider.id, {
            name: m.name.trim(),
            upstream_model_name: m.upstream_model_name.trim(),
            protocol: m.protocol,
            context_window: m.context_window,
            input_cost_per_ktok: m.input_cost_per_ktok,
            output_cost_per_ktok: m.output_cost_per_ktok,
            cache_write_cost_per_ktok: m.cache_write_cost_per_ktok,
            cache_read_cost_per_ktok: m.cache_read_cost_per_ktok,
            rpm_limit: m.rpm_limit,
            tpm_limit: m.tpm_limit,
          });
        } catch (e: any) {
          errors.push(`${m.name}: ${e.message || "create failed"}`);
        }
      }

      if (errors.length > 0) {
        toast.warning(
          `Provider created, but some models failed: ${errors.join("; ")}`
        );
      } else {
        toast.success("Provider and models created successfully");
      }
      navigate("/console/admin/providers");
    } catch (e: any) {
      toast.error(e.message || "Failed to create provider");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConsoleLayout title="New Provider">
      <div className="flex flex-col h-full bg-background">
        <div className="p-6 overflow-auto max-w-4xl w-full mx-auto">
          <div className="mb-6">
            <Link href="/console/admin/providers">
              <Button variant="ghost" size="sm" className="mb-3">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Providers
              </Button>
            </Link>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Provider Setup Wizard
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Create a new upstream provider and its initial Provider Models in one
              flow.
            </p>
          </div>

          <StepIndicator current={step} total={TOTAL_STEPS} />

          <div className="border border-border rounded-xl bg-card p-6 mt-6">
            {step === 1 && (
              <Step1Basics
                basics={basics}
                setBasics={setBasics}
              />
            )}
            {step === 2 && (
              <Step2Credentials
                backend={basics.backend}
                apiKey={apiKey}
                setApiKey={setApiKey}
                googleServiceAccountJson={googleServiceAccountJson}
                setGoogleServiceAccountJson={setGoogleServiceAccountJson}
                show={showKey}
                setShow={setShowKey}
              />
            )}
            {step === 3 && (
              <Step3Models
                models={models}
                parentProtocol={basics.protocol}
                updateModel={updateModel}
                addModel={addModel}
                removeModel={removeModel}
              />
            )}
            {step === 4 && (
              <Step4Confirm
                basics={basics}
                hasKey={apiKey.length > 0}
                models={models}
              />
            )}
          </div>

          <div className="flex justify-between mt-6">
            <Button
              variant="ghost"
              onClick={back}
              disabled={step === 1 || submitting}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            {step < TOTAL_STEPS ? (
              <Button onClick={next}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting}>
                {submitting ? "Creating..." : "Create Provider"}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </ConsoleLayout>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  const labels = ["Basics", "Credentials", "Models", "Confirm"];
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => {
        const n = i + 1;
        const active = n === current;
        const done = n < current;
        return (
          <div key={n} className="flex items-center flex-1">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold transition-colors ${
                done
                  ? "bg-primary text-primary-foreground"
                  : active
                  ? "bg-primary/15 text-primary border-2 border-primary"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {done ? <Check className="w-4 h-4" /> : n}
            </div>
            <span
              className={`ml-2 text-xs font-medium ${
                active ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {labels[i]}
            </span>
            {n < total && (
              <div
                className={`flex-1 h-px mx-3 ${done ? "bg-primary" : "bg-border"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step1Basics({
  basics,
  setBasics,
}: {
  basics: ProviderBasics;
  setBasics: (fn: (b: ProviderBasics) => ProviderBasics) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-base">Provider basics</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Name, slug, base URL, and supported protocols.
        </p>
      </div>
      <div className="space-y-2">
        <Label>Name</Label>
        <Input
          value={basics.name}
          onChange={(e) => setBasics((b) => ({ ...b, name: e.target.value }))}
          placeholder="e.g. Anthropic Production"
        />
      </div>
      <div className="space-y-2">
        <Label>Slug</Label>
        <Input
          value={basics.slug}
          onChange={(e) => setBasics((b) => ({ ...b, slug: e.target.value }))}
          placeholder="e.g. anthropic-prod"
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground">
          Lowercase letters, numbers, and hyphens only. Used in routing IDs.
        </p>
      </div>
      <div className="space-y-2">
        <Label>Protocol</Label>
        <select
          value={basics.protocol}
          onChange={(e) => {
            const protocol = e.target.value as Protocol;
            setBasics((b) => ({ ...b, protocol, backend: protocol === "anthropic" ? b.backend : "direct" }));
          }}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="anthropic">Anthropic</option>
          <option value="openai-chat">OpenAI Chat</option>
          <option value="openai-response">OpenAI Response</option>
        </select>
        <p className="text-xs text-muted-foreground">
          The single upstream protocol this provider speaks.
        </p>
      </div>
      {basics.protocol === "anthropic" && (
        <div className="space-y-2">
          <Label>Backend</Label>
          <select
            value={basics.backend}
            onChange={(e) => setBasics((b) => ({ ...b, backend: e.target.value as "direct" | "vertex" }))}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="direct">Direct (first-party API)</option>
            <option value="vertex">Google Vertex AI</option>
          </select>
          <p className="text-xs text-muted-foreground">
            How this provider connects to the upstream service.
          </p>
        </div>
      )}      {basics.backend === "direct" ? (

      <div className="space-y-2">
        <Label>Base URL</Label>
        <Input
          value={basics.base_url}
          onChange={(e) => setBasics((b) => ({ ...b, base_url: e.target.value }))}
          placeholder="https://api.anthropic.com/v1"
        />
        <p className="text-xs text-muted-foreground">Include the version path (e.g. /v1); the endpoint suffix is appended automatically.</p>
      </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>GCP Project ID</Label>
            <Input
              value={basics.project}
              onChange={(e) => setBasics((b) => ({ ...b, project: e.target.value }))}
              placeholder="e.g. my-google-project"
            />
          </div>
          <div className="space-y-2">
            <Label>Location</Label>
            <Input
              value={basics.location}
              onChange={(e) => setBasics((b) => ({ ...b, location: e.target.value }))}
              placeholder="e.g. us-central1"
            />
          </div>
        </>
      )}
    </div>
  );
}

function Step2Credentials({
  backend,
  apiKey,
  setApiKey,
  googleServiceAccountJson,
  setGoogleServiceAccountJson,
  show,
  setShow,
}: {
  backend: "direct" | "vertex";
  apiKey: string;
  setApiKey: (s: string) => void;
  googleServiceAccountJson: string;
  setGoogleServiceAccountJson: (s: string) => void;
  show: boolean;
  setShow: (b: boolean) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-base">{backend === "direct" ? "API Key" : "Service Account JSON"}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {backend === "direct" 
            ? "Upstream credential, encrypted at rest with the configured KEK."
            : "Google Cloud Service Account JSON key for Vertex AI access."}
        </p>
      </div>
      {backend === "direct" ? (
        <div className="space-y-2">
          <Label>API Key</Label>
          <div className="flex gap-2">
            <Input
              type={show ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-..."
              className="font-mono text-sm"
              autoComplete="off"
            />
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={() => setShow(!show)}
              title={show ? "Hide" : "Show"}
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Service Account JSON</Label>
          <textarea
            value={googleServiceAccountJson}
            onChange={(e) => setGoogleServiceAccountJson(e.target.value)}
            placeholder={`{
  "type": "service_account",
  "project_id": "...",
  ...
}`}
            className="w-full min-h-[250px] font-mono text-xs p-3 rounded-md border border-input bg-background"
            autoComplete="off"
          />
        </div>
      )}
    </div>
  );
}

function Step3Models({
  models,
  parentProtocol,
  updateModel,
  addModel,
  removeModel,
}: {
  models: ProviderModelDraft[];
  parentProtocol: Protocol;
  updateModel: (idx: number, patch: Partial<ProviderModelDraft>) => void;
  addModel: () => void;
  removeModel: (idx: number) => void;
}) {
  const { t } = useLanguage();
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-base">Provider Models</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Add at least one upstream model with its cost rates (in USD per 1k tokens).
          </p>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={addModel}>
          <Plus className="w-4 h-4 mr-1" />
          Add Model
        </Button>
      </div>

      {models.map((m, idx) => (
        <div
          key={idx}
          className="border border-border rounded-lg p-4 space-y-3 bg-muted/20"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Model #{idx + 1}
            </span>
            {models.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeModel(idx)}
                title="Remove model"
              >
                <Trash2 className="w-4 h-4 text-destructive/70" />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Display Name</Label>
              <Input
                value={m.name}
                onChange={(e) => updateModel(idx, { name: e.target.value })}
                placeholder="e.g. Claude 3.5 Sonnet"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Upstream Model Name</Label>
              <Input
                value={m.upstream_model_name}
                onChange={(e) =>
                  updateModel(idx, { upstream_model_name: e.target.value })
                }
                placeholder="e.g. claude-3-5-sonnet-20241022"
                className="font-mono text-sm"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Protocol</Label>
            <p className="text-sm text-muted-foreground uppercase tracking-wide">{parentProtocol}</p>
            <p className="text-xs text-muted-foreground">Inherited from the provider.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Context Window</Label>
              <Input
                type="number"
                min={1}
                value={m.context_window}
                onChange={(e) =>
                  updateModel(idx, { context_window: Number(e.target.value) || 0 })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("admin.providerModels.rpmLimit")}</Label>
              <Input
                type="number"
                min={0}
                placeholder={t("admin.providerModels.unlimited")}
                value={m.rpm_limit ?? ""}
                onChange={(e) =>
                  updateModel(idx, {
                    rpm_limit: e.target.value ? parseInt(e.target.value, 10) : null,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("admin.providerModels.tpmLimit")}</Label>
              <Input
                type="number"
                min={0}
                placeholder={t("admin.providerModels.unlimited")}
                value={m.tpm_limit ?? ""}
                onChange={(e) =>
                  updateModel(idx, {
                    tpm_limit: e.target.value ? parseInt(e.target.value, 10) : null,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Input $/ktok</Label>
              <Input
                type="number"
                step="0.0001"
                min={0}
                value={m.input_cost_per_ktok}
                onChange={(e) =>
                  updateModel(idx, {
                    input_cost_per_ktok: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Output $/ktok</Label>
              <Input
                type="number"
                step="0.0001"
                min={0}
                value={m.output_cost_per_ktok}
                onChange={(e) =>
                  updateModel(idx, {
                    output_cost_per_ktok: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cache Write $/ktok</Label>
              <Input
                type="number"
                step="0.0001"
                min={0}
                value={m.cache_write_cost_per_ktok}
                onChange={(e) =>
                  updateModel(idx, {
                    cache_write_cost_per_ktok: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cache Read $/ktok</Label>
              <Input
                type="number"
                step="0.0001"
                min={0}
                value={m.cache_read_cost_per_ktok}
                onChange={(e) =>
                  updateModel(idx, {
                    cache_read_cost_per_ktok: Number(e.target.value) || 0,
                  })
                }
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Step4Confirm({
  basics,
  hasKey,
  models,
}: {
  basics: ProviderBasics;
  hasKey: boolean;
  models: ProviderModelDraft[];
}) {
  const { t } = useLanguage();
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-base">Confirm &amp; Create</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Review the configuration. Submitting creates the Provider and its Models.
          Binding to Science LLMs happens separately on the Science LLMs page.
        </p>
      </div>
      <div className="space-y-1 text-sm">
        <SummaryRow label="Name" value={basics.name} />
        <SummaryRow label="Slug" value={basics.slug} mono />
        <SummaryRow label="Protocol" value={basics.protocol} />
        {basics.backend === "direct" && <SummaryRow label="Base URL" value={basics.base_url} mono />}
        <SummaryRow label="Backend" value={basics.backend} />
        {basics.backend === "vertex" && (
          <>
            <SummaryRow label="Project" value={basics.project} mono />
            <SummaryRow label="Location" value={basics.location} mono />
          </>
        )}
        <SummaryRow label="API Key" value={hasKey ? "Provided" : "Not set"} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Provider Models ({models.length})
        </div>
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Upstream</th>
                <th className="px-3 py-2 text-left">Protocol</th>
                <th className="px-3 py-2 text-right">Ctx</th>
                <th className="px-3 py-2 text-right">{t("admin.providerModels.rpmLimit")}</th>
                <th className="px-3 py-2 text-right">{t("admin.providerModels.tpmLimit")}</th>
                <th className="px-3 py-2 text-right">In / Out</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {models.map((m, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">{m.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                    {m.upstream_model_name}
                  </td>
                  <td className="px-3 py-2">{m.protocol}</td>
                  <td className="px-3 py-2 text-right">{m.context_window}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{m.rpm_limit ?? <span className="italic">{t("admin.providerModels.unlimited")}</span>}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{m.tpm_limit ?? <span className="italic">{t("admin.providerModels.unlimited")}</span>}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {m.input_cost_per_ktok} / {m.output_cost_per_ktok}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex">
      <div className="w-32 text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono text-xs" : ""}>{value || "—"}</div>
    </div>
  );
}
